import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
  TextFilterProps,
} from "@amzn/awsui-components-console";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  TableEmptyState,
  TableNoMatchState,
} from "../commons/common-components";
import { RagaAvailability } from "@amzn/fuse-ard-service-v2-client";
import { useFlareContext } from "../../context/flare/flareContext";

type RagaAvailabilities = RagaAvailability[];

const COLUMN_DEFINITIONS = [
  {
    id: "buyingCustomerCountryCode",
    header: "Buying customer country code",
    cell: (item: RagaAvailability) => item.buyingCustomerCountryCode,
    width: 100,
    sortingField: "buyingCustomerCountryCode",
  },
  {
    id: "product",
    header: "Product",
    cell: (item: RagaAvailability) => item.product,
    width: 100,
    sortingField: "product",
  },
  {
    id: "term",
    header: "Term",
    cell: (item: RagaAvailability) => item.term,
    width: 100,
    sortingField: "term",
  },
  {
    id: "ragaEnabled",
    header: "Raga enabled in",
    cell: (item: RagaAvailability) => item.ragaEnabled,
    width: 100,
  },
];

const RagaAvailabilityTable = () => {
  const {
    ragaAvailabilities,
    lastUpdatedOn,
    loadingRagaAvailabilities,
    error,
    listRagaAvailabilities,
    clearError,
  } = useFlareContext();

  const textFilterRef = useRef<TextFilterProps.Ref>(null);
  const [cacheAge, setCacheAge] = useState<string>();

  const calculateCacheAge = (lastUpdatedOn: Date | undefined): string => {
    if (!lastUpdatedOn) {
      return "";
    }
    const currentTime = new Date();
    const timeDifference = currentTime.getTime() - lastUpdatedOn.getTime();
    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? "s" : ""} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    } else {
      return `${seconds} second${seconds > 1 ? "s" : ""} ago`;
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCacheAge(calculateCacheAge(lastUpdatedOn));
    }, 1000);

    return () => clearInterval(interval);
  }, [lastUpdatedOn]);

  useEffect(() => {
    textFilterRef.current?.focus();
  }, []);

  // Load items on mount
  useEffect(() => {
    listRagaAvailabilities(false);
  }, [listRagaAvailabilities]);

  const handleFetchFromFlare = async () => {
    clearError();
    await listRagaAvailabilities(true);
  };

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(ragaAvailabilities || [], {
    filtering: {
      fields: ["buyingCustomerCountryCode"],
      empty: <TableEmptyState resourceName="Raga availability" />,
      noMatch: (
        <TableNoMatchState onClearFilter={() => actions.setFiltering("")} />
      ),
    },
    pagination: {
      pageSize: 100,
    },
    sorting: {
      defaultState: {
        sortingColumn: COLUMN_DEFINITIONS[0],
      },
    },
    selection: {},
  });
  const selectedItems = collectionProps.selectedItems!;

  return (
    <SpaceBetween size="m">
      {lastUpdatedOn && (
        <Alert
          action={
            <Button
              onClick={handleFetchFromFlare}
              disabled={loadingRagaAvailabilities}
              disabledReason="Fetching from Flare"
            >
              Fetch from Flare
            </Button>
          }
        >
          {`Last fetched from Flare on ${lastUpdatedOn} (${cacheAge || calculateCacheAge(lastUpdatedOn)}).`}
        </Alert>
      )}
      <Table
        {...collectionProps}
        wrapLines={false}
        loading={loadingRagaAvailabilities}
        loadingText="Loading Raga availabilities"
        stickyHeaderVerticalOffset={1}
        variant="full-page"
        header={
          <Header
            variant="awsui-h1-sticky"
            counter={
              ragaAvailabilities
                ? selectedItems!.length
                  ? `(${selectedItems!.length}/${ragaAvailabilities.length})`
                  : `(${ragaAvailabilities.length})`
                : undefined
            }
            actions={null}
          >
            Raga availabilities
          </Header>
        }
        columnDefinitions={COLUMN_DEFINITIONS}
        items={items}
        pagination={<Pagination {...paginationProps} />}
        filter={
          <TextFilter
            {...filterProps}
            ref={textFilterRef}
            countText={`${filteredItemsCount} match(es)`}
            filteringPlaceholder="Filter by country code"
          />
        }
      />
    </SpaceBetween>
  );
};

export default RagaAvailabilityTable;
